<div class="page-container" [class.dialog-mode]="isDialogMode">
  <!-- Main Content -->
  <main class="page-main">
    <!-- Page Content -->
    <div class="page-content">
      <!-- Header Section -->
      <div class="page-title-container" [class.hidden]="isDialogMode">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">Matter Details</h1>
        @if (matter && !isLoading && !error && isComparisonPopUpsEnabled) {
          <div class="page-title-actions">
            <div class="title-action" (click)="openInFloatingDialog()">
              <mat-icon class="title-action-icon">select_window_2</mat-icon>
              <span class="title-action-label">Open in dialog</span>
            </div>
          </div>
        }
      </div>

      <!-- Loading State -->
      @if (isLoading) {
        <mat-card class="matter-details-card">
          <div class="loading-state">
            <mat-spinner diameter="32"></mat-spinner>
            <p>Loading matter details...</p>
          </div>
        </mat-card>
      }

      <!-- Error State -->
      @if (error && !isLoading) {
        <app-error-state
          [message]="error"
          buttonLabel="Go Back"
          (retry)="goBack()"
        ></app-error-state>
      }

      <!-- Top Row: Matter Profile and Client Details -->
      @if (matter && !isLoading && !error) {
        <div class="top-row-layout">
          <!-- Matter Profile Card (2/3 width) -->
          <mat-card class="matter-details-card matter-profile-card">
            <div class="matter-details">
              <!-- Matter Header -->
              <div class="matter-profile">
                <div class="matter-info">
                  <div class="name-and-id">
                    <h1 class="matter-name">
                      <div class="title-and-id">
                        {{ matter.matterName }}
                        <app-copyable-text
                          [value]="getMatterIdForCopy()"
                          [displayText]="getMatterIdDisplay()"
                          copyTitle="Click to copy matter ID"
                        />
                      </div>
                      <div class="status-container">
                        <span class="badge" [ngClass]="getStatusBadgeClass()">
                          {{ getMatterStatus() }}
                        </span>
                        <span class="date-range-subtext">
                          {{ getDateRangeText() }}
                        </span>
                      </div>
                    </h1>
                  </div>

                  <!-- Basic Information Section -->
                  <div class="basic-info-divider"></div>
                  <div class="basic-info-section">
                    <div class="details-grid">
                      <div class="detail-item">
                        <span class="detail-label">Matter Number</span>
                        <span class="detail-value">{{
                          matter.matterNumber
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Open Date</span>
                        <span class="detail-value">{{
                          matter.openDate
                            ? (matter.openDate | date: "shortDate")
                            : "Not set"
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Billing Partner</span>
                        <span class="detail-value">{{
                          matter.billingPartner
                            ? matter.billingPartner.firstName +
                              " " +
                              matter.billingPartner.lastName
                            : "Not provided"
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Close Date</span>
                        <span class="detail-value">{{
                          matter.closeDate
                            ? (matter.closeDate | date: "shortDate")
                            : "Not set"
                        }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Supervising Attorney</span>
                        <span class="detail-value">{{
                          matter.supervisor
                            ? matter.supervisor.firstName +
                              " " +
                              matter.supervisor.lastName
                            : "Not provided"
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-card>

          <!-- Client Details Card (1/3 width) -->
          @if (matter.client) {
            <mat-card class="matter-details-card client-details-card">
              <div class="matter-details">
                <div class="client-details-section">
                  <div class="client-header">
                    <div class="client-name-section">
                      <span class="client-name-label">Client Name</span>
                      <h2 class="client-name-header">
                        {{ matter.client.clientName }}
                      </h2>
                    </div>
                    <span class="client-details-label">Client Details</span>
                  </div>
                  <div class="client-details-divider"></div>
                  <div class="client-details-grid">
                    <div class="detail-item">
                      <span class="detail-label">Client Number</span>
                      <span class="detail-value">{{
                        matter.client.clientNumber
                      }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Industry</span>
                      <span class="detail-value">
                        {{
                          matter.client.industry?.description || "Not specified"
                        }}
                      </span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Street</span>
                      <span class="detail-value">{{
                        matter.client.street || "Not specified"
                      }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">City, State, Zip Code</span>
                      <span class="detail-value">
                        {{ matter.client.city || "Not specified" }},
                        {{ matter.client.state || "Not specified" }}
                        {{ matter.client.zipCode || "" }}
                      </span>
                    </div>
                    <div class="detail-item">
                      <span class="detail-label">Country</span>
                      <span class="detail-value">{{
                        matter.client.country || "Not specified"
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </mat-card>
          }
        </div>

        <div class="matter-details-content">
          <!-- Billing History, then Pending Assignments, each full width -->
          <div class="billing-assignments-stack">
            <!-- Billing History Card -->
            <mat-card class="matter-details-card billing-history-card">
              <app-matter-billing-history
                [clientMatterId]="matter.clientMatterNumber"
                [breadcrumbContext]="getMatterBreadcrumbContext()"
              ></app-matter-billing-history>
            </mat-card>

            <!--
              Pending Assignments Card

              showViewAllNotes is true because this page renders the
              Assignment Notes card that the action scrolls to, not because of a
              permission: the card offers the action whenever the host does, and
              which notes the Assignment Notes card then shows is decided by the
              API — the viewer's own note needs no permission at all.
            -->
            <mat-card class="matter-details-card pending-assignments-card">
              <app-assignments-card
                mode="matter"
                [assignments]="assignmentRows"
                [filterResetKey]="clientMatterNumber"
                [showPracticeAreaSummary]="true"
                [showViewAllNotes]="true"
                (viewAllNotesClicked)="scrollToAssignmentNotes()"
                [isLoading]="isLoadingAssignments"
                [canEdit]="canEditAssignments"
                [canCreate]="canCreateAssignments"
                [canRemove]="canDeleteAssignments"
                [canReadResourceNotes]="canReadResourceAssignmentNotes"
                [showManagerNotes]="canReadManagerAssignmentNotes"
                [canWriteManagerNotes]="canWriteManagerAssignmentNotes"
                (resourceClicked)="navigateToResource($event)"
                (removeClicked)="onRemoveAssignment($event)"
                (assignmentRemoved)="loadAssignmentsAfterRemove($event)"
                (assignmentChanged)="onAssignmentChanged($event)"
                [showAddAssignment]="canCreateAssignments"
                [addAssignmentDisabled]="isAddingAssignment"
                [showFilter]="!isAddingAssignment"
                (addAssignmentClicked)="startAddAssignment()"
              >
                @if (canCreateAssignments && isAddingAssignment) {
                  <!-- Escape is bound here rather than on the typeahead input, which
                       is replaced once a resource is staged — the point at which
                       there is most to discard. keydown bubbles from whichever
                       field has focus; an open datepicker calendar lives in a CDK
                       overlay outside this subtree, so its own Escape is unaffected. -->
                  <div
                    class="add-assignment-form-container"
                    (keydown.escape)="cancelAddAssignment()"
                  >
                    <!-- Column widths come from .add-assignment-form-table in
                         _add-assignment-typeahead.scss, shared with resource details. -->
                    <table class="add-assignment-form-table">
                      <thead>
                        <tr>
                          <th>Resource</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="inline-add-row">
                          <td class="inline-add-cell assignment-name-cell">
                            @if (stagedResource) {
                              <div class="staged-matter-label">
                                <span>{{ stagedResource.fullName }}</span>
                                @if (stagedResource.title) {
                                  <span class="staged-matter-number">{{
                                    stagedResource.title
                                  }}</span>
                                }
                              </div>
                            } @else {
                              <div class="typeahead-wrapper">
                                <mat-icon class="typeahead-search-icon"
                                  >search</mat-icon
                                >
                                <input
                                  #addAssignmentInput
                                  type="text"
                                  class="typeahead-input"
                                  placeholder="Add a resource by name..."
                                  [value]="addResourceQuery"
                                  (input)="onAddResourceInput($event)"
                                  (blur)="onAddResourceBlur()"
                                  [disabled]="isCreatingAssignment"
                                  autocomplete="off"
                                />
                                @if (addResourceQuery) {
                                  <mat-icon
                                    class="typeahead-suffix-icon"
                                    (click)="clearAddResourceTypeahead()"
                                    >close</mat-icon
                                  >
                                }
                              </div>
                              @if (isSearchingResources) {
                                <div class="typeahead-status">
                                  <mat-spinner diameter="16"></mat-spinner>
                                  <span>Searching...</span>
                                </div>
                              } @else if (resourceSuggestions.length > 0) {
                                <div
                                  class="typeahead-suggestions"
                                  role="listbox"
                                  aria-label="Resource suggestions"
                                >
                                  @for (
                                    r of resourceSuggestions;
                                    track r.employeeId
                                  ) {
                                    <div
                                      role="option"
                                      class="typeahead-suggestion"
                                      (mousedown)="onSelectResourceToAssign(r)"
                                    >
                                      <span class="suggestion-name">{{
                                        r.fullName
                                      }}</span>
                                      @if (r.title) {
                                        <span class="suggestion-title">{{
                                          r.title
                                        }}</span>
                                      }
                                    </div>
                                  }
                                </div>
                              } @else if (
                                addResourceQuery.trim().length >= 3 &&
                                resourceNotFound
                              ) {
                                <div class="typeahead-status">
                                  <span>No matching resources found.</span>
                                </div>
                              }
                            }
                          </td>
                          <td class="inline-add-cell">
                            <mat-form-field
                              appearance="outline"
                              class="inline-add-field"
                            >
                              <input
                                matInput
                                [matDatepicker]="startPicker"
                                [(ngModel)]="stagedStartDate"
                                placeholder="mm/dd/yyyy"
                                [disabled]="
                                  !stagedResource || isCreatingAssignment
                                "
                              />
                              <mat-datepicker-toggle
                                matSuffix
                                [for]="startPicker"
                                [disabled]="
                                  !stagedResource || isCreatingAssignment
                                "
                              ></mat-datepicker-toggle>
                              <mat-datepicker #startPicker></mat-datepicker>
                            </mat-form-field>
                          </td>
                          <td class="inline-add-cell">
                            <mat-form-field
                              appearance="outline"
                              class="inline-add-field"
                            >
                              <input
                                matInput
                                [matDatepicker]="endPicker"
                                [(ngModel)]="stagedEndDate"
                                [min]="stagedStartDate"
                                placeholder="mm/dd/yyyy"
                                [disabled]="
                                  !stagedResource || isCreatingAssignment
                                "
                              />
                              <mat-datepicker-toggle
                                matSuffix
                                [for]="endPicker"
                                [disabled]="
                                  !stagedResource || isCreatingAssignment
                                "
                              ></mat-datepicker-toggle>
                              <mat-datepicker #endPicker></mat-datepicker>
                            </mat-form-field>
                          </td>
                          <td class="inline-add-actions-cell">
                            @if (isCreatingAssignment) {
                              <mat-spinner diameter="18"></mat-spinner>
                            } @else {
                              <div class="inline-add-actions">
                                <button
                                  mat-icon-button
                                  class="inline-save-btn"
                                  matTooltip="Assign"
                                  matTooltipClass="light-tooltip"
                                  type="button"
                                  [disabled]="!stagedResource"
                                  (click)="onConfirmAssignment()"
                                >
                                  <mat-icon>check</mat-icon>
                                </button>
                                <button
                                  mat-icon-button
                                  class="inline-cancel-btn"
                                  matTooltip="Cancel"
                                  matTooltipClass="light-tooltip"
                                  type="button"
                                  (click)="cancelAddAssignment()"
                                >
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                }
              </app-assignments-card>
            </mat-card>
          </div>

          <!-- Notes Row: Assignment Notes | Matter Notes -->
          <div class="notes-row">
            <!--
              Every assignment note on this matter, shown by default. It is fed
              the list the page already holds, so it costs no second request;
              while that list loads it gets null, so the card shows its loading
              state rather than briefly reading as empty, and a failed load shows
              the card's error rather than "no notes".
            -->
            <mat-card
              class="matter-details-card assignment-notes-card"
              tabindex="-1"
              role="region"
              aria-label="Assignment notes"
              #assignmentNotesSection
            >
              <app-assignment-notes-panel
                mode="matter"
                [clientMatterNumber]="matter.clientMatterNumber"
                [entityName]="matter.matterName"
                [assignments]="isLoadingAssignments ? null : assignments"
                [loadFailed]="assignmentsLoadFailed"
              />
            </mat-card>

            <mat-card class="matter-details-card notes-card">
              <app-entity-notes
                [entityId]="matter.clientMatterNumber"
                [entityType]="'matter'"
                [notesInfo]="matter.notesInfo"
                [isReadOnly]="isNotesReadOnly"
                [breadcrumbContext]="getMatterBreadcrumbContext()"
                title="Matter Notes"
                placeholder="Click Edit to add notes about this matter..."
                (notesSaved)="onNotesSaved($event)"
                (editModeRequested)="onNotesEditModeRequested()"
              ></app-entity-notes>
            </mat-card>
          </div>
        </div>
      }
    </div>
  </main>
</div>
